import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import styled from "styled-components";
import FormContainer, {
  CheckboxField,
  InputField,
  SelectField,
  SubmitButton,
} from "components/form";
import { usePostAdminUserOverrideUserConfigMutation } from "store/admin/api/apiSlice";
import UILabel from "components/ui/UILabel";
import { Heading } from "components/ui/Text";
import {
  ApiAdminUser,
  ApiAdminUserEwaMultiplierByRepaymentCount,
} from "utils/api/admin/user";
import { Pre } from "components/ui/Pre";
import { RepaymentMethod } from "utils/api/user";
import { mediaMin } from "utils/theme";
import Modal, { ModalApi } from "./Modal";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-height: 70vh;
  overflow-y: auto;

  text-align: left;
`;

const WideModal = styled(Modal)`
  & > div > div {
    width: 800px;
    max-width: 90vw;
  }
`;

const Row = styled.div`
  display: grid;
  gap: 6px;

  ${mediaMin("md")} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StyledHr = styled.hr`
  width: 100%;
  margin: 0px;
`;

interface Props {
  user: ApiAdminUser;
  onSuccess: () => void;
}

export const REPAYMENT_METHOD_OPTIONS = [
  { name: "Ach Debit", value: RepaymentMethod.achDebitFlow },
  { name: "Direct Deposit", value: RepaymentMethod.directDepositFlow },
];

const OverrideUserConfigModal = (
  { user, onSuccess }: Props,
  forwardedRef: React.ForwardedRef<unknown>
) => {
  const ref = useRef<ModalApi>(null);
  useImperativeHandle(forwardedRef, () => ref.current);
  const [postOverrideUserConfig, postOverrideUserConfigContext] =
    usePostAdminUserOverrideUserConfigMutation();

  const onSubmit = (values: {
    customRepaymentPlan?: boolean;
    enableProbationaryLimit?: boolean;
    ewaMultiplier?: number;
    ewaMultiplierByRepaymentCount?: ApiAdminUserEwaMultiplierByRepaymentCount;
    inactive?: boolean;
    requirePayrollLinking?: boolean;
    repaymentMethod?: RepaymentMethod;
  }) => {
    const overrideParams = {
      id: user.id,
      ...values,
    };

    // This checks for presence because 0 is a valid value.
    if ("ewaMultiplier" in values) {
      const multiplier = values.ewaMultiplier;
      const isValidMultiplier =
        multiplier !== null &&
        multiplier !== undefined &&
        !Number.isNaN(multiplier);

      delete overrideParams.ewaMultiplier;
      if (isValidMultiplier) {
        // Right now we only support overriding the multipliers with a single value.
        // If we need to support multiple values, we can add a new field in the future
        // that supports an array of values.
        overrideParams.ewaMultiplierByRepaymentCount = {
          payrollLinked: [multiplier],
          payrollUnlinked: [multiplier],
        };
      }
    }

    postOverrideUserConfig(overrideParams);
  };

  useEffect(() => {
    if (postOverrideUserConfigContext.isSuccess && onSuccess) {
      onSuccess();
    }

    ref.current?.dismiss();
  }, [postOverrideUserConfigContext.isSuccess]);

  return (
    <WideModal ref={ref}>
      <Wrapper>
        <Heading.H4 tag="h2">Override User Configs</Heading.H4>

        <Pre json={user.userConfig} />
        <StyledHr />

        <FormContainer onSubmit={onSubmit}>
          <SelectField
            name="repaymentMethod"
            label="Repayment Method"
            options={REPAYMENT_METHOD_OPTIONS}
            defaultValue={user.userConfig.repaymentMethod}
            required
          />

          <Row>
            <CheckboxField
              name="requirePayrollLinking"
              label="Require Payroll Linking"
              defaultChecked={user.userConfig.requirePayrollLinking}
            />

            <CheckboxField
              name="enableProbationaryLimit"
              label="Enable Probationary Limit"
              defaultChecked={user.userConfig.enableProbationaryLimit}
            />
          </Row>

          <Row>
            <CheckboxField
              name="customRepaymentPlan"
              label="Custom Repayment Plan"
              defaultChecked={user.userConfig.customRepaymentPlan}
            />

            <CheckboxField
              name="inactive"
              label="Inactive"
              defaultChecked={user.userConfig.inactive}
            />
          </Row>

          <Row>
            <InputField
              name="ewaMultiplier"
              label="Earned Wage Access Multiplier"
              placeholder="0.5"
              type="number"
              step="0.01"
            />

            <InputField
              name="incomeTaxRefundAdvanceMultiplier"
              label="Income Tax Refund Advance Multiplier"
              defaultValue={user.userConfig.incomeTaxRefundAdvanceMultiplier}
              placeholder="0.5"
              type="number"
              step="0.01"
            />
          </Row>
          <UILabel.Small>
            Multipliers must be a percentage between 0 and 1.
          </UILabel.Small>

          <SubmitButton>Override</SubmitButton>
        </FormContainer>
      </Wrapper>
    </WideModal>
  );
};

export default forwardRef(OverrideUserConfigModal);
